import React, { useState, useEffect, memo } from "react";

import AddIcon from "@mui/icons-material/Add";
import {
  getPracticeClaimCPTRecommendationDetails,
  getPracticeClaimICDRecommendationDetails,
  updatePracticeClaimCPTRecommendationDetails,
  updatePracticeClaimICDRecommendationDetails,
  getProcedure,
  getModifier,
  deleteManualRecommendationCode,
  getDiagnosisRisk,
} from "apiHandler/actions";
import { Button, LinearProgress, Snackbar, Alert, Box } from "@mui/material";
import { useUserData } from "components/customHooks/userDataContext";

import CodeTable from "components/modals/claimDetails/codeTable";
import {
  cptRightColumns,
  icdRightColumns,
} from "components/modals/claimDetails/helper";

import MedCodeDialog from "components/modals/medCodeDialog";
import ScoreComparison from "components/modals/claimDetails/scoreComparison";
import AcceptRejectBtn from "./acceptRejectBtn";

const collapseSections = [
  "clinical_justification",
  "supporting_evidence",
  "documentation_requirements",
  "user_decision_notes",
];

const AiMedCodes = ({ details, readOnly }) => {
  const [aiRows, setAiRows] = useState([]);
  const [aiPriRows, setAiPriRows] = useState([]);
  const [aiSecRows, setAiSecRows] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [addCodeDialogOpen, setAddCodeDialogOpen] = useState(false);
  const userData = useUserData();
  const token = userData?.access_token;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [selectedCodeType, setSelectedCodeType] = useState("CPT");

  // For multiple medical codes and their inputs
  const [selectedCodes, setSelectedCodes] = useState([null]);
  const [inputValues, setInputValues] = useState([""]);
  const [noOfBoxes, setNoOfBoxes] = useState(9);

  // Modifiers are arrays of four elements per code
  const [modifierInputsList, setModifierInputsList] = useState([["", ""]]);
  const [modifierValuesList, setModifierValuesList] = useState([[null, null]]);
  const [searchedModifiersList, setSearchedModifiersList] = useState([
    [[], []],
  ]);
  const [loadingModifiersList, setLoadingModifiersList] = useState([
    [false, false],
  ]);
  const [modifierDebounceTimeoutsList, setModifierDebounceTimeoutsList] =
    useState([[null, null]]);

  // For code search results and loading
  const [searchedCodes, setSearchedCodes] = useState([]);
  const [loadingCode, setLoadingCode] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  // Selections for bulk updates (keep these if you use them)
  const [selectedIcdRows, setSelectedIcdRows] = useState([]);
  const [selectedCptPriRows, setSelectedCptPriRows] = useState([]);
  const [selectedCptQuaRows, setSelectedCptQuaRows] = useState([]);

  useEffect(() => {
    getClaimDetails();
  }, []);

  const getClaimDetails = async () => {
    setLoading(true);
    setAiRows([]);
    setAiPriRows([]);
    setAiSecRows([]);
    try {
      const payload = {
        practice_id: `${details.practice_id}`,
        dictation_id: `${details.id}`,
      };

      const resIcd = await getPracticeClaimICDRecommendationDetails(
        payload,
        token,
      );

      const resCpt = await getPracticeClaimCPTRecommendationDetails(
        payload,
        token,
      );

      const aiData = resIcd.filter(
        (item) => item.recommendation_type !== "original",
      );
      const aiCptData = resCpt.filter(
        (item) => item.recommendation_type !== "original",
      );

      const aiPrimaryCodes = aiCptData.filter(
        (row) => Number(row.rvu_value) > 0,
      );
      const aiQualityCodes = aiCptData.filter(
        (row) => Number(row.rvu_value) === 0,
      );

      setAiRows(aiData);
      setAiPriRows(aiPrimaryCodes);
      setAiSecRows(aiQualityCodes);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const searchCodes = async (input) => {
    if (!input) return;
    setLoadingCode(true);
    let response;
    const payload = {
      type: selectedCodeType === "CPT" ? "Procedure" : "Diagnosis",
      search_by: input,
    };
    if (selectedCodeType === "CPT")
      response = await getProcedure(payload, token);
    if (selectedCodeType === "ICD10")
      response = await getDiagnosisRisk(payload, token);

    setSearchedCodes(response || []);
    setLoadingCode(false);
  };

  const handleInputChange = (idx, value) => {
    setInputValues((prev) => {
      const updated = [...prev];
      updated[idx] = value;
      return updated;
    });

    if (debounceTimeout) clearTimeout(debounceTimeout);

    const timeout = setTimeout(() => {
      searchCodes(value);
    }, 1500);

    setDebounceTimeout(timeout);
  };

  const searchModifier = async (codeIdx, modIdx, input) => {
    if (!input) return;
    setLoadingModifiersList((prev) => {
      const updated = [...prev];
      if (!updated[codeIdx]) updated[codeIdx] = [false, false];
      updated[codeIdx][modIdx] = true;
      return updated;
    });

    const res = await getModifier(
      { type: "Modifier", search_by: input },
      userData?.billing_access_token,
    );

    setSearchedModifiersList((prev) => {
      const updated = [...prev];
      if (!updated[codeIdx]) updated[codeIdx] = [[], []];
      updated[codeIdx][modIdx] = res || [];
      return updated;
    });

    setLoadingModifiersList((prev) => {
      const updated = [...prev];
      if (!updated[codeIdx]) updated[codeIdx] = [false, false];
      updated[codeIdx][modIdx] = false;
      return updated;
    });
  };

  const handleModifierInput = (codeIdx, modIdx) => (e, value) => {
    setModifierInputsList((prev) => {
      const updated = [...prev];
      if (!updated[codeIdx]) updated[codeIdx] = ["", ""];
      updated[codeIdx][modIdx] = value;
      return updated;
    });

    if (modifierDebounceTimeoutsList[codeIdx]?.[modIdx])
      clearTimeout(modifierDebounceTimeoutsList[codeIdx][modIdx]);

    const timeout = setTimeout(() => {
      searchModifier(codeIdx, modIdx, value);
    }, 1500);

    setModifierDebounceTimeoutsList((prev) => {
      const updated = [...prev];
      if (!updated[codeIdx]) updated[codeIdx] = [null, null];
      updated[codeIdx][modIdx] = timeout;
      return updated;
    });
  };

  const handleModifierValue = (codeIdx, modIdx, val) => {
    setModifierValuesList((prev) => {
      const updated = [...prev];
      if (!updated[codeIdx]) updated[codeIdx] = [null, null];
      updated[codeIdx][modIdx] = val;
      return updated;
    });
  };

  const removeCodeField = (idx) => {
    setSelectedCodes((prev) => prev.filter((_, i) => i !== idx));
    setInputValues((prev) => prev.filter((_, i) => i !== idx));
    setModifierInputsList((prev) => prev.filter((_, i) => i !== idx));
    setModifierValuesList((prev) => prev.filter((_, i) => i !== idx));
    setSearchedModifiersList((prev) => prev.filter((_, i) => i !== idx));
    setLoadingModifiersList((prev) => prev.filter((_, i) => i !== idx));
    setModifierDebounceTimeoutsList((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleOpenAddCodeDialog = () => {
    setAddCodeDialogOpen(true);
  };

  const onSubmit = async (selectedRow, action, section, code) => {
    const isIcd = section === "ICD";
    setSnackbarSeverity("info");
    setSnackbarMessage("Submitting...");
    setSnackbarOpen(true);
    try {
      const payload = {
        practice_id: details.practice_id,
        recommendation_id: selectedRow?.recommendation_id,
        user_decision: action,
        user_name: userData.first_name + " " + userData.last_name,
        ...(isIcd ? { icd_code: code } : { cpt_code: code }),
      };
      let res;
      if (isIcd) {
        res = await updatePracticeClaimICDRecommendationDetails(payload, token);
      } else {
        res = await updatePracticeClaimCPTRecommendationDetails(payload, token);
      }
      if (res && res?.status === "successfull") {
        setSnackbarSeverity("success");
        setSnackbarMessage("Submission successful");
        setSnackbarOpen(true);
        getClaimDetails();
      }
    } catch (error) {
      console.log("error: ", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Submission failed. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleCancel = () => {
    setAddCodeDialogOpen(false);

    setSelectedCodes([null]);
    setInputValues([""]);
    setModifierInputsList([["", ""]]);
    setModifierValuesList([[null, null]]);
    setSearchedModifiersList([[[], []]]);
    setLoadingModifiersList([[false, false]]);
    setModifierDebounceTimeoutsList([[null, null]]);
    setNoOfBoxes(9);
    setLoadingCode(false);
  };

  const handleBulkUpdate = async (action, type) => {
    try {
      setIsSaving(true);
      // Find the actual code objects by id from aiRows/aiPriRows/aiSecRows
      const payload = (
        type === "ICD"
          ? selectedIcdRows
          : type === "CPT Pri"
            ? selectedCptPriRows
            : selectedCptQuaRows
      ).map((row) => ({
        practice_id: details.practice_id,
        user_decision: action,
        user_name: `${userData.first_name} ${userData.last_name}`,
        recommendation_id: row.recommendation_id,
        ...(type === "ICD"
          ? { icd_code: row.icd_code }
          : { cpt_code: row.cpt_code }),
      }));

      // Choose correct update function and call
      let res;
      if (type === "ICD") {
        res = await updatePracticeClaimICDRecommendationDetails(payload, token);
      } else {
        res = await updatePracticeClaimCPTRecommendationDetails(payload, token);
      }
      if (res && res?.status === "successfull") {
        setSnackbarSeverity("success");
        setSnackbarMessage("Update successful");
        setSnackbarOpen(true);
        getClaimDetails();
        setSelectedCptPriRows([]);
        setSelectedCptQuaRows([]);
        setSelectedIcdRows([]);
      }
    } catch (error) {
      console.log("error: ", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Bulk update failed");
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const onDeleteCode = async (row, type) => {
    setSnackbarSeverity("info");
    setSnackbarMessage("Deleting...");
    setSnackbarOpen(true);
    try {
      const payload = {
        practice_id: details.practice_id,
        dictation_id: details.id,
        code_type: type === "ICD" ? "ICD10" : type,
        recommendation_id: row?.recommendation_id,
        user_name: userData.first_name + " " + userData.last_name,
        ...(type === "ICD"
          ? { icd_code: row.icd_code }
          : { cpt_code: row.cpt_code }),
      };
      const res = await deleteManualRecommendationCode(payload, token);
      console.log("res: ", res);
      if (res && res.status === "successfull") {
        setSnackbarSeverity("success");
        setSnackbarMessage(
          `${
            type === "ICD" ? row?.icd_code : row.cpt_code
          }Recommendation Code deleted`,
        );
        setSnackbarOpen(true);
        getClaimDetails();
      }
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Error while deleting the code. Please try again!");
      setSnackbarOpen(true);
      console.log("error: ", error);
    }
  };

  return (
    <div className="dictation-medcode" style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <h3>Medical Codes</h3>
        {!readOnly && (
          <Button
            variant="text"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddCodeDialog}
          >
            Add Code
          </Button>
        )}
      </div>

      {loading && <LinearProgress />}

      <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
        <ScoreComparison
          aiScore={aiRows[0]?.ai_recommended_raf_score}
          showOriginal={false}
          isIcd={true}
        />

        <ScoreComparison
          aiScore={aiPriRows[0]?.ai_recommended_rvu_total}
          maxValue={15}
          showOriginal={false}
        />
      </Box>

      <AcceptRejectBtn
        acceptTitle={" Accept ICD Codes"}
        rejectTitle={"Reject ICD Codes"}
        disable={!selectedIcdRows.length}
        onAccept={() => handleBulkUpdate("accepted", "ICD")}
        onReject={() => handleBulkUpdate("rejected", "ICD")}
      />
      <CodeTable
        title="AI ICD Codes"
        rows={aiRows}
        columns={icdRightColumns((row, action, section) => {
          onSubmit(row, action, section, row.icd_code);
        }, onDeleteCode)}
        loading={loading}
        collapseSections={collapseSections}
        selectedRows={selectedIcdRows}
        setSelectedRows={setSelectedIcdRows}
        multiSelection={true}
      />

      <AcceptRejectBtn
        acceptTitle={" Accept CPT Primary Codes"}
        rejectTitle={"Reject CPT Primary Codes"}
        disable={!selectedCptPriRows.length}
        onAccept={() => handleBulkUpdate("accepted", "CPT Pri")}
        onReject={() => handleBulkUpdate("rejected", "CPT Pri")}
      />
      <CodeTable
        title="AI CPT Primary Codes"
        rows={aiPriRows}
        columns={cptRightColumns((row, action, section) => {
          onSubmit(row, action, section, row.cpt_code);
        }, onDeleteCode)}
        loading={loading}
        collapseSections={collapseSections}
        selectedRows={selectedCptPriRows}
        setSelectedRows={setSelectedCptPriRows}
        multiSelection={true}
      />
      <AcceptRejectBtn
        acceptTitle={" Accept CPT Quality Codes"}
        rejectTitle={"Reject CPT Quality Codes"}
        disable={!selectedCptQuaRows.length}
        onAccept={() => handleBulkUpdate("accepted", "CPT")}
        onReject={() => handleBulkUpdate("rejected", "CPT")}
      />
      <CodeTable
        title="AI CPT Quality Codes"
        rows={aiSecRows}
        columns={cptRightColumns((row, action, section) => {
          onSubmit(row, action, section, row.cpt_code);
        }, onDeleteCode)}
        loading={loading}
        collapseSections={collapseSections}
        selectedRows={selectedCptQuaRows}
        setSelectedRows={setSelectedCptQuaRows}
        multiSelection={true}
      />

      <MedCodeDialog
        open={addCodeDialogOpen}
        onClose={handleCancel}
        handleModifierInput={handleModifierInput}
        handleModifierValue={handleModifierValue}
        loadingCode={loadingCode}
        onInputChange={handleInputChange}
        searchedCodes={searchedCodes}
        selectedCodeType={selectedCodeType}
        setSelectedCodeType={setSelectedCodeType}
        isSaving={isSaving}
        inputValues={inputValues}
        loadingModifiersList={loadingModifiersList}
        modifierInputsList={modifierInputsList}
        modifierValuesList={modifierValuesList}
        removeCodeField={removeCodeField}
        searchedModifiersList={searchedModifiersList}
        selectedCodes={selectedCodes}
        setSelectedCodes={setSelectedCodes}
        setInputValues={setInputValues}
        setLoadingModifiersList={setLoadingModifiersList}
        setModifierDebounceTimeoutsList={setModifierDebounceTimeoutsList}
        setModifierInputsList={setModifierInputsList}
        setModifierValuesList={setModifierValuesList}
        noOfBoxes={noOfBoxes}
        setNoOfBoxes={setNoOfBoxes}
        setSearchedModifiersList={setSearchedModifiersList}
        details={details}
        getClaimDetails={getClaimDetails}
        setAddCodeDialogOpen={setAddCodeDialogOpen}
        setIsSaving={setIsSaving}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarSeverity={setSnackbarSeverity}
        userData={userData}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default memo(AiMedCodes);
